import { isOk, Ok, Result } from "@pacote/result"
import { useQuery } from "@tanstack/react-query"
import { z } from "zod"

import { ListboxOption } from "@/components/ui/select-field"

import { baseApiResponseSchema } from "./api-config"

/* -------------------------------------------------------------------------------------------------
 * types
 * -----------------------------------------------------------------------------------------------*/

const countrySchema = z.object({
  description: z.string(),
  fiscalCode: z.string(),
  sign: z.string(),
  sign3: z.string(),
  callingCodes: z.array(z.string()).optional(),
  actual: z.boolean(),
})

// eslint-disable-next-line @typescript-eslint/no-unused-vars
const countriesResponseSchema = baseApiResponseSchema.extend({
  countries: z.array(countrySchema),
})

type CountriesResponse = z.infer<typeof countriesResponseSchema>

type CountriesServiceResponse = Result<
  {
    normalizedCountries: { [key: string]: ListboxOption & { sign: string } }
    phonePrefixCountries: ListboxOption[]
    countries: CountriesResponse["countries"]
  },
  void
>

/* -------------------------------------------------------------------------------------------------
 * config
 * -----------------------------------------------------------------------------------------------*/
const countriesServiceEndpoint = "/residence-core-ms/v1/geo/countries"

/*-------------------------------------------------------------------------------------------------
 * helpers
 * -----------------------------------------------------------------------------------------------*/

function transformCountriesResponse(countries: CountriesResponse["countries"]) {
  const normalizedCountries: {
    [key: string]: ListboxOption & { sign: string }
  } = {}
  const phonePrefixCountries: ListboxOption[] = []

  countries.forEach((country) => {
    const { sign, callingCodes } = country
    const iconSrc = sign
      ? `/countries-flags/${sign}.svg`
      : `/countries-flags/- .svg`

    if (callingCodes) {
      for (let j = 0, len = callingCodes.length; j < len; j++) {
        const callingCode = callingCodes[j]
        const optionId = "+" + callingCode
        if (callingCode) {
          phonePrefixCountries.push({
            optionId,
            label: optionId,
            iconSrc,
          })
        }
      }
    }

    normalizedCountries[country.description] = {
      label: country.description,
      optionId: country.fiscalCode,
      iconSrc,
      sign: country.sign,
    }
  })

  return { normalizedCountries, phonePrefixCountries, countries }
}

/* -------------------------------------------------------------------------------------------------
 * fetcher
 * -----------------------------------------------------------------------------------------------*/
const countriesFetcher = async (): Promise<CountriesServiceResponse> => {
  const countries = await import("@/config/countries-es.json")
  return Ok(transformCountriesResponse(countries.default || []))
}

/* -------------------------------------------------------------------------------------------------
 * hooks
 * -----------------------------------------------------------------------------------------------*/

const useCountriesQuery = () => {
  const { data, isLoading, isFetching, ...rest } = useQuery({
    queryKey: [countriesServiceEndpoint],
    queryFn: async () => countriesFetcher(),
  })

  const normalizedCountries =
    data && isOk(data) ? data.value.normalizedCountries : {}

  return {
    countries: data,
    normalizedCountries,
    isCountriesQueryLoading: isLoading || isFetching,
    ...rest,
  }
}

export { countriesFetcher, useCountriesQuery }
export type { CountriesServiceResponse }
